import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import 'nprogress/nprogress.css';
import App from 'src/App';
import { SidebarProvider } from 'src/contexts/SidebarContext';
import * as serviceWorker from 'src/serviceWorker';
import { store } from './redux/store';
import { Provider } from 'react-redux';
import { API_URL, fetchData } from './common/config/TextMessage';
import { QueryProvider } from 'src/react-query/QueryProvider';
import { isMaintenanceMode, isMaintenanceAllowedIPs } from 'src/appConfig';
import ThemeProvider from './theme/ThemeProvider';
import { ErrorBoundary } from './bugsnag';
import { CircularProgress, CssBaseline } from '@mui/material';
import { localStorageMethod } from './common/config/FunctionHelper';
const StatusCheck = lazy(() => import('./content/pages/Status/StatusCheck'));
const StatusMaintenance = lazy(
  () => import('./content/pages/Status/StatusMaintenance')
);
/**
 * Fetches a predetermined response from a specific URL to set access permissions.
 * If the response status is not 200, access is denied by setting `isAllowed` to false.
 */
const allowedIp = async () => {
  try {
    // Attempt to fetch the response from the specified URL
    const rp = await fetch(API_URL, {
      method: 'GET',
      credentials: 'include'
      // headers: { 'Content-Type': 'application/json' }
    });

    // If the response status is not OK, set access as not allowed
    if (rp?.status !== 200) {
      return false;
    }

    // If everything is fine, return true to allow access
    return true;
  } catch (error) {
    // If an error occurs (e.g., network failure, server error), log the error and return false
    console.error('Error fetching API:', error);
    return false;
  }
};

/**
 * Checks if the user's IP is allowed by fetching their current IP and comparing it with a list of allowed IPs.
 * If `restrictAccessByIP` is true, it performs the check; otherwise, it directly calls `allowedIp`.
 */
const authenticateIPAccess = async () => {
  if (!isMaintenanceMode) {
    return true; // No maintenance mode, allow access
  }

  try {
    const response = await fetch('https://api.ipify.org?format=json');
    if (!response.ok) {
      throw new Error('Failed to fetch IP address');
    }
    const data = await response?.json();
    const ipAddress = data?.ip; // The user's current IP address

    // Check if the user's IP matches any of the allowed IPs
    if (isMaintenanceAllowedIPs?.includes(ipAddress)) {
      return await allowedIp(); // IP is allowed, further IP check
    } else {
      return false; // IP not in allowed list, deny access
    }
  } catch (error) {
    console.error('Error fetching IP address:', error);
    return false; // Error occurred, deny access
  }
};

/**
 * Status component that renders while in maintenance mode.
 */
const SuspenseFallback = () => <StatusMaintenance />;

/**
 * AppContainer component to render the main application.
 */
const AppContainer = () => {
  const [isAllowed, setIsAllowed] = useState(isMaintenanceMode ? false : true);
  const [isApiUp, setIsApiUp] = useState(true);
  const [isCheckingApi, setIsCheckingApi] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const isApiDown = localStorageMethod('get', 'api_down') == 'true';

  const apiUpFunction = async (currentRetry = retryCount) => {
    const allowed = await authenticateIPAccess(); // Only called once
    setIsAllowed(allowed);
    const apiSuccess = await allowedIp();
    if (apiSuccess) {
      setIsApiUp(true);
      localStorageMethod('remove', 'api_down');
      setIsCheckingApi(false);
    } else if (currentRetry < 2) {
      setIsCheckingApi(true);
      const nextRetry = currentRetry + 1;
      setRetryCount(nextRetry);
      if (currentRetry == 1) {
        setIsApiUp(false);
      }
      setTimeout(() => apiUpFunction(currentRetry + 1), 3000); // <– use passed retry value
    } else {
      // In development, we don't want to block the app even if the API is down
      if (process.env.NODE_ENV === 'development') {
        setIsApiUp(true);
      } else {
        setIsApiUp(false);
      }
      setIsCheckingApi(false);
    }
  };
  if (isApiDown) {
    apiUpFunction();
  }
  useEffect(() => {
    if (isApiDown) {
      apiUpFunction();
    }
  }, [isApiDown]);

  useEffect(() => {
    (async () => {
      const allowed = await authenticateIPAccess(); // Only called once
      setIsAllowed(allowed);
      if (allowed) {
        await apiUpFunction();
      }
    })();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={SuspenseFallback}>
      <Suspense fallback={<SuspenseFallback />}>
        <Provider store={store}>
          <QueryProvider>
            <HelmetProvider>
              <SidebarProvider>
                <BrowserRouter>
                  {isCheckingApi ? (
                    <div
                      style={{
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                      }}
                    >
                      <CircularProgress size={50} sx={{ color: 'var(--color-primary)' }} />{' '}
                      {/* Circular spinner */}
                      <div style={{ marginTop: '20px' }}>{fetchData}</div>
                    </div>
                  ) : isAllowed && isApiUp && !isCheckingApi ? (
                    <App />
                  ) : (
                    <ThemeProvider>
                      <CssBaseline />
                      {!isApiUp && !isMaintenanceMode ? (
                        <StatusCheck />
                      ) : (
                        <StatusMaintenance />
                      )}
                    </ThemeProvider>
                  )}
                </BrowserRouter>
              </SidebarProvider>
            </HelmetProvider>
          </QueryProvider>
        </Provider>
      </Suspense>
    </ErrorBoundary>
  );
};

// Use createRoot for concurrent rendering in React 18
const root = createRoot(document.getElementById('root'));
root.render(<AppContainer />);

serviceWorker.unregister();
